/**
 * i18n Helper Utilities
 * 
 * This module provides utility functions for handling multilingual field names
 * and localized content across the application. It supports dynamic field naming
 * pattern where fields are suffixed with the language code (e.g., field_ar, field_fr).
 * 
 * Benefits:
 * - Cleaner, more readable code compared to ternary operators
 * - Easier to add new language support in the future
 * - Centralized location for i18n logic
 * - Improved maintainability across the codebase
 * 
 * Usage Examples:
 * 
 * 1. Get field by language suffix:
 *    const fieldName = getLocalizedField('numero_mise', i18n.language);
 *    // Returns: 'numero_mise_ar' or 'numero_mise_fr' depending on i18n.language
 * 
 * 2. Access object property with dynamic field:
 *    const value = rowData[getLocalizedField('numero_mise', i18n.language)];
 * 
 * 3. Get text direction for RTL languages:
 *    const dir = getTextDirection(i18n.language); // 'rtl' or 'ltr'
 * 
 * 4. Get text alignment for RTL languages:
 *    const align = getTextAlignment(i18n.language); // 'right' or 'left'
 */

/**
 * Get localized field name by appending language suffix
 * 
 * @param {string} fieldBase - Base field name (e.g., 'numero_mise', 'Libelle')
 * @param {string} language - Language code (e.g., 'ar', 'fr')
 * @returns {string} Localized field name (e.g., 'numero_mise_ar')
 * 
 * @example
 * getLocalizedField('numero_mise', 'ar')  // Returns: 'numero_mise_ar'
 * getLocalizedField('Libelle', 'fr')      // Returns: 'Libelle_fr'
 */
export const getLocalizedField = (fieldBase, language) => {
  if (!fieldBase || !language) {
    console.warn('getLocalizedField: fieldBase and language parameters are required');
    return fieldBase;
  }
  return `${fieldBase}_${language}`;
};

/**
 * Get text direction based on language
 * Useful for RTL (Right-to-Left) languages like Arabic
 * 
 * @param {string} language - Language code
 * @returns {string} Text direction ('rtl' or 'ltr')
 * 
 * @example
 * getTextDirection('ar')  // Returns: 'rtl'
 * getTextDirection('fr')  // Returns: 'ltr'
 */
export const getTextDirection = (language) => {
  const rtlLanguages = ['ar', 'he', 'ur', 'fa']; // RTL languages
  return rtlLanguages.includes(language) ? 'rtl' : 'ltr';
};

/**
 * Get text alignment based on language
 * Useful for RTL (Right-to-Left) languages like Arabic
 * 
 * @param {string} language - Language code
 * @returns {string} Text alignment ('right' or 'left')
 * 
 * @example
 * getTextAlignment('ar')  // Returns: 'right'
 * getTextAlignment('fr')  // Returns: 'left'
 */
export const getTextAlignment = (language) => {
  const rtlLanguages = ['ar', 'he', 'ur', 'fa']; // RTL languages
  return rtlLanguages.includes(language) ? 'right' : 'left';
};

/**
 * Check if a language uses RTL (Right-to-Left) text direction
 * 
 * @param {string} language - Language code
 * @returns {boolean} True if language is RTL, false otherwise
 * 
 * @example
 * isRtlLanguage('ar')  // Returns: true
 * isRtlLanguage('fr')  // Returns: false
 */
export const isRtlLanguage = (language) => {
  const rtlLanguages = ['ar', 'he', 'ur', 'fa'];
  return rtlLanguages.includes(language);
};

/**
 * Get localized value from an object using dynamic field naming
 * 
 * @param {Object} obj - Object containing localized fields
 * @param {string} fieldBase - Base field name
 * @param {string} language - Language code
 * @returns {*} The value of the localized field, or undefined if not found
 * 
 * @example
 * const user = { name_ar: 'محمد', name_fr: 'Mohamed' };
 * getLocalizedValue(user, 'name', 'ar')  // Returns: 'محمد'
 */
export const getLocalizedValue = (obj, fieldBase, language) => {
  if (!obj || !fieldBase || !language) {
    console.warn('getLocalizedValue: obj, fieldBase, and language parameters are required');
    return undefined;
  }
  const localizedField = getLocalizedField(fieldBase, language);
  return obj[localizedField];
};

/**
 * Batch get multiple localized values from an object
 * 
 * @param {Object} obj - Object containing localized fields
 * @param {string[]} fieldBases - Array of base field names
 * @param {string} language - Language code
 * @returns {Object} Object with base field names as keys and localized values as values
 * 
 * @example
 * const user = {
 *   name_ar: 'محمد',
 *   name_fr: 'Mohamed',
 *   email_ar: 'user@example.com',
 *   email_fr: 'user@example.com'
 * };
 * getLocalizedValues(user, ['name', 'email'], 'ar')
 * // Returns: { name: 'محمد', email: 'user@example.com' }
 */
export const getLocalizedValues = (obj, fieldBases, language) => {
  if (!obj || !Array.isArray(fieldBases) || !language) {
    console.warn('getLocalizedValues: obj, fieldBases array, and language parameters are required');
    return {};
  }
  
  const result = {};
  fieldBases.forEach(fieldBase => {
    result[fieldBase] = getLocalizedValue(obj, fieldBase, language);
  });
  return result;
};

/**
 * Create a localized style object for text alignment
 * 
 * @param {string} language - Language code
 * @returns {Object} Style object with textAlign property
 * 
 * @example
 * <div style={getLocalizedStyle('ar')}>
 *   Right-aligned text
 * </div>
 * // In Arabic, text will be right-aligned
 */
export const getLocalizedStyle = (language) => {
  return {
    textAlign: getTextAlignment(language)
  };
};

/**
 * Create a complete localized body style for DataTable columns
 * 
 * @param {string} language - Language code
 * @returns {Object} Style object with textAlign property for table body cells
 * 
 * @example
 * <Column
 *   bodyStyle={getColumnBodyStyle('ar')}
 *   field="name"
 * />
 */
export const getColumnBodyStyle = (language) => {
  return {
    textAlign: getTextAlignment(language)
  };
};

const i18nHelpers = {
  getLocalizedField,
  getTextDirection,
  getTextAlignment,
  isRtlLanguage,
  getLocalizedValue,
  getLocalizedValues,
  getLocalizedStyle,
  getColumnBodyStyle
};

export default i18nHelpers;
